import React from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Column} from '../types';
import {apiService} from '../services/api';
import {ConfirmModal} from './ConfirmModal';
import {SaveFilterModal} from './SaveFilterModal';
import {IconDeviceFloppy, IconFilter, IconStar, IconStarFilled, IconTrash, IconX} from "@tabler/icons-react";
import {FilterGroupComponent, FilterGroup} from './FilterGroup';
import {buildFiltersFromGroup} from '../utils/filterUtils';

interface ScreenDataTableFiltersProps {
    screenId: string;
    filterableColumns: Column[];
    showFilters: boolean;
    setShowFilters: (show: boolean) => void;
    filterGroup: FilterGroup;
    setFilterGroup: (group: FilterGroup) => void;
    appliedFilterGroup: FilterGroup | null;
    setAppliedFilterGroup: (group: FilterGroup | null) => void;
    originalFilterGroup: FilterGroup | null;
    setOriginalFilterGroup: (group: FilterGroup | null) => void;
    selectedUserFilter: string | null;
    setSelectedUserFilter: (id: string | null) => void;
    savedFilters: any[];
    operatorsByColumn: Record<string, { value: string; label: string }[]>;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
    embedded?: boolean;
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
}

export const ScreenDataTableFilters: React.FC<ScreenDataTableFiltersProps> = ({
    screenId,
    filterableColumns,
    showFilters,
    setShowFilters,
    filterGroup,
    setFilterGroup,
    appliedFilterGroup,
    setAppliedFilterGroup,
    originalFilterGroup,
    setOriginalFilterGroup,
    selectedUserFilter,
    setSelectedUserFilter,
    savedFilters,
    operatorsByColumn,
    onSuccess,
    onError,
    embedded = false,
    pageSize,
    onPageSizeChange
}) => {
    const queryClient = useQueryClient();
    const [showSaveFilterModal, setShowSaveFilterModal] = React.useState(false);
    const [showUpdateFilterModal, setShowUpdateFilterModal] = React.useState(false);
    const [showConfirmDeleteFilter, setShowConfirmDeleteFilter] = React.useState(false);

    const saveFilterMutation = useMutation({
        mutationFn: (name: string) => {
            const currentFilterData = buildFiltersFromGroup(filterGroup);
            return apiService.createUserFilter(screenId, name, currentFilterData);
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({queryKey: ["userFilters", screenId]});
            setAppliedFilterGroup(filterGroup);
            setOriginalFilterGroup(filterGroup);

            const newFilterId = response.data?.id || response.data?.data?.id;
            if (newFilterId) {
                setSelectedUserFilter(newFilterId);
            }

            queryClient.invalidateQueries({queryKey: ["screenData", screenId]});
            onSuccess('Filter uložený');
        },
        onError: () => {
            onError('Chyba pri ukladaní filtra');
        }
    });

    const updateFilterMutation = useMutation({
        mutationFn: ({filterId, name, conditions}: { filterId: string, name: string, conditions: any }) => {
            return apiService.updateUserFilter(filterId, name, conditions);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["userFilters", screenId]});
            setAppliedFilterGroup(filterGroup);
            setOriginalFilterGroup(filterGroup);
            queryClient.invalidateQueries({queryKey: ["screenData", screenId]});
            onSuccess('Filter upravený');
        },
        onError: () => {
            onError('Chyba pri úprave filtra');
        }
    });

    const deleteFilterMutation = useMutation({
        mutationFn: (filterId: string) => apiService.deleteUserFilter(filterId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["userFilters", screenId]});
            setSelectedUserFilter(null);
            onSuccess('Filter zmazaný');
        },
        onError: () => {
            onError('Chyba pri mazaní filtra');
        }
    });

    const setDefaultMutation = useMutation({
        mutationFn: ({filterId, isDefault}: { filterId: string, isDefault: boolean }) =>
            apiService.setUserFilterDefault(filterId, isDefault),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["userFilters", screenId]});
        },
        onError: () => {
            onError('Chyba pri nastavení predvoleného filtra');
        }
    });

    const selectedFilterObj = savedFilters.find((f: any) => f.id === selectedUserFilter);
    const selectedIsDefault = !!selectedFilterObj?.isDefault;

    const handleConfirmDeleteFilter = () => {
        if (selectedUserFilter) {
            deleteFilterMutation.mutate(selectedUserFilter);
        }
        setShowConfirmDeleteFilter(false);
    };

    const hasFilterChanged = selectedUserFilter && originalFilterGroup &&
        JSON.stringify(filterGroup) !== JSON.stringify(originalFilterGroup);

    const filterGroups = buildFiltersFromGroup(appliedFilterGroup || filterGroup);

    if (filterableColumns.length === 0) {
        return (
            <div className="text-muted small py-2">
                Žiadne filtrovateľné stĺpce v tejto obrazovke. V admine pri stĺpci nastav <strong>Filtrovateľné</strong>.
            </div>
        );
    }

    const hasUnappliedChanges = JSON.stringify(filterGroup) !== JSON.stringify(appliedFilterGroup ?? {operator: 'AND', conditions: []});
    const hasActiveFilter = !!appliedFilterGroup && (appliedFilterGroup.conditions?.length ?? 0) > 0;

    return (
        <>
            <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center flex-wrap mb-2">
                {/* Left side — apply / cancel / save */}
                <div className="d-flex flex-column flex-md-row gap-2">
                    {filterGroups && (
                        <button
                            className="btn btn-pill btn-primary btn-sm"
                            type="button"
                            onClick={() => setAppliedFilterGroup(filterGroup)}
                            disabled={!hasUnappliedChanges}
                            title="Filtrovať bez uloženia"
                        >
                            <IconFilter size={16} className="me-1"/>
                            Filtrovať
                        </button>
                    )}

                    {hasActiveFilter && (
                        <button
                            className="btn btn-pill btn-outline-secondary btn-sm"
                            type="button"
                            onClick={() => setAppliedFilterGroup(null)}
                            title="Zrušiť filter"
                        >
                            <IconX size={16} className="me-1"/>
                            Zrušiť
                        </button>
                    )}

                    {!selectedUserFilter && filterGroups && (
                        <button
                            className="btn btn-pill btn-outline-success btn-sm"
                            type="button"
                            onClick={() => setShowSaveFilterModal(true)}
                            title="Uložiť aktuálny filter"
                        >
                            <IconDeviceFloppy size={16} className="me-1"/>
                            Uložiť
                        </button>
                    )}
                </div>

                {/* Right side — edit / delete saved filter, save-as-new */}
                {(selectedUserFilter || (selectedUserFilter && hasFilterChanged)) && (
                    <div className="d-flex flex-column flex-md-row gap-2 ms-md-auto">
                        {selectedUserFilter && hasFilterChanged && (
                            <button
                                className="btn btn-pill btn-outline-success btn-sm"
                                type="button"
                                onClick={() => setShowSaveFilterModal(true)}
                                title="Uložiť ako nový filter"
                            >
                                <IconDeviceFloppy size={16} className="me-1"/>
                                Uložiť nový
                            </button>
                        )}
                        {selectedUserFilter && (
                            <>
                                <button
                                    className={`btn btn-pill btn-sm ${selectedIsDefault ? 'btn-warning text-white' : 'btn-outline-warning'}`}
                                    type="button"
                                    onClick={() => setDefaultMutation.mutate({filterId: selectedUserFilter, isDefault: !selectedIsDefault})}
                                    title={selectedIsDefault ? 'Odstrániť ako predvolený filter' : 'Nastaviť ako predvolený filter'}
                                >
                                    {selectedIsDefault
                                        ? <IconStarFilled size={16} className="me-1"/>
                                        : <IconStar size={16} className="me-1"/>}
                                    Predvolený
                                </button>
                                <button
                                    className="btn btn-pill btn-outline-primary btn-sm"
                                    type="button"
                                    onClick={() => setShowUpdateFilterModal(true)}
                                    title={hasFilterChanged ? 'Upraviť existujúci filter' : 'Upraviť filter'}
                                >
                                    <IconDeviceFloppy size={16} className="me-1"/>
                                    Upraviť
                                </button>
                                <button
                                    className="btn btn-pill btn-outline-danger btn-sm"
                                    type="button"
                                    onClick={() => setShowConfirmDeleteFilter(true)}
                                    title="Zmazať filter"
                                >
                                    <IconTrash size={16} className="me-1"/>
                                    Zmazať
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            <FilterGroupComponent
                group={filterGroup}
                columns={filterableColumns}
                onChange={setFilterGroup}
                operatorsByColumn={operatorsByColumn}
            />

            <SaveFilterModal
                show={showSaveFilterModal}
                onHide={() => setShowSaveFilterModal(false)}
                onSave={(name) => saveFilterMutation.mutate(name)}
            />

            <SaveFilterModal
                show={showUpdateFilterModal}
                onHide={() => setShowUpdateFilterModal(false)}
                onSave={(name) => {
                    if (selectedUserFilter) {
                        const currentFilterData = buildFiltersFromGroup(filterGroup);
                        updateFilterMutation.mutate({
                            filterId: selectedUserFilter,
                            name,
                            conditions: currentFilterData
                        });
                    }
                }}
                existingName={selectedUserFilter ? savedFilters.find((f: any) => f.id === selectedUserFilter)?.name : ''}
            />

            <ConfirmModal
                show={showConfirmDeleteFilter}
                onHide={() => setShowConfirmDeleteFilter(false)}
                onConfirm={handleConfirmDeleteFilter}
                title="Zmazať filter"
                message="Naozaj chcete zmazať tento uložený filter?"
                confirmText="Zmazať"
                cancelText="Zrušiť"
                variant="danger"
            />
        </>
    );
};
